import {DefaultError, QueryClient, useQuery, UseQueryOptions, UseQueryResult} from "@tanstack/react-query";
import {APIPromise, APIResponse} from "services/HttpService";
import {easasAlert, easasErrorAlert} from "../../components/fragments/easas-global-fragment/EasasGlobalAlerts";


export interface IQueryOptions<T = any> {
        enabled?: boolean;
        noCache?: boolean;
        gcTime?: number
        staleTime?: number;
        refetchInterval?: number | boolean;
        onSuccess?: (data?: T) => void;
        onError?: (data?: T) => void;
        retry?: boolean|number,
        refetchOnWindowFocus?: boolean;
    }


    export interface IMutationOptions {
        onSuccess?: (data?: any) => void;
        onFailure?: (data?: any) => void;
        onMutate?: (data?: any) => void;
    }

    export enum QueryKey {
        DATASET_CATEGORIES = "dataset-categories",
        DATASET_DATA_INFO = "dataset-data-info",
        DATASET_INFOS = "dataset-infos",
        USER_INFO = "user-info",
        BASE_DATASETS = "base-datasets",
        AIRFLOW_DAG_TASK_INFOS = "airflow-dag-task-infos",
        PRODUCT_TEMPLATE_INFOS = "product-template-infos",
        PAGINATED_DATASETS = "paginated-datasets-list",
        DATASETS_COUNT = "count",
        PRODUCT_TEMPLATE_CONFIGS = "product-template-configs",
        PRODUCT_TEMPLATE_DATA = "product-template-data",
        PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA = "product-template-preview-filter-data",
        SCHEDULED_TASK_HISTORIES = "scheduled-task-histories",
        PRODUCT_TEMPLATE_FOREIGN_KEYS = "product-template-foreign-keys",
        DASHBOARD = "dashboard",
        GENERATED_REPORT_DATA = "generated-report-data",
        ALERT_FAN = "alert-fan",
        ALERT_FAN_SILENCE = "alert-fan-silence",
        INSTANT_JOBS = "instant-jobs",
        DEFAULT_CATEGORY = "default-category",
        CATEGORIZED_DATASET = "categorized-dataset",
        CLEAR_DATASET = "clear-dataset",
        USER_SESSIONS = "user-sessions",
        EASASJS_BUILDS = "easasjs-builds",
        UPDATE_DATASET = "update-dataset",
        GEO_MEASUREMENT_DEVICES = "geo-measurement-devices",
        MEASUREMENT_DEVICES_READINGS = "measurement-devices-readings",
        MEASUREMENT_DEVICES_BY_TAGS = "measurement-devices-by-tags",
        DATASET_MAPPERS = "dataset-mappers",
    }

    export function defaultResponseHandler<T>(res: APIResponse<T>): T | undefined {
        if (res.error) {
            console.log(res)
            throw res.error;
        } else {
            return res.data;
        }
    }

    const defaultQueryOptions: IQueryOptions = {
        enabled: true,
        noCache: false,
        refetchInterval: false,
    }

    export const defaultMutationOptions: IMutationOptions = Object.freeze({
        onSuccess: () => {},
        onFailure: () => {},
        onMutate: () => {},
    })

    export const defaultDeleteMutationOptions: IMutationOptions = Object.freeze({
        onSuccess: () => {
            easasAlert("Deleted");
        },
        onFailure: () => {
            easasErrorAlert("Error deleting");
        },
        onMutate: () => {},
    });

    export function useQueryWrapper<T>(
        queryKey: Array<QueryKey | string | null | undefined>,
        apiPromiseFn: () => APIPromise<T>,
        promiseHandler?: ((res: APIResponse<T>) => T) | null,
        options?: IQueryOptions
    ): UseQueryResult<T , Error> {
        const usedOptions = {...defaultQueryOptions, ...options};
        const usedPromiseHandler = promiseHandler ?? defaultResponseHandler;

        const queryOptions = {
            enabled: usedOptions.enabled,
            gcTime: usedOptions.gcTime ?? 1000 * 60 * 5,
            staleTime: usedOptions.staleTime ?? 1000 * 60,
            refetchInterval: usedOptions.refetchInterval,
            onSuccess: usedOptions.onSuccess,
            onError: usedOptions.onError,
            retry: usedOptions.retry,
            refetchOnWindowFocus: usedOptions.refetchOnWindowFocus,
            queryKey: queryKey
        } as UseQueryOptions<unknown, DefaultError, T>

        if (usedOptions.noCache) {
            queryOptions.gcTime = 0;
            queryOptions.staleTime = 0;
        }

        return useQuery({queryFn: () => apiPromiseFn().then(usedPromiseHandler), ...queryOptions})
    }

    export const getRelatedCacheKeys = (queryClient: QueryClient, targetKey: string) => {
        return queryClient.getQueryCache().getAll()
            .map(query => query.queryKey)
            .filter(key => key.includes(targetKey))
    }